import React, {
  Fragment,
  createContext,
  useContext,
  type ReactNode,
} from 'react';

import styled from 'styled-components';
import Markdown from 'marked-react';

import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { Link } from '@grnhse/seedling/lib/birch/typography';

import { useChatTypewriter } from './use_chat_typewriter';
import {
  assistantMessageBubbleStyles,
  assistantMessageContentStyles,
  chatMessageTextStyles,
} from './message_styles';

type Props = {
  content: string;
  animate?: boolean;
  footer?: ReactNode;
};

function safeHttpsUrl(href?: string): string {
  try {
    const url = new URL(href ?? '');
    return url.protocol === 'https:' ? href ?? '#' : '#';
  } catch {
    return '#';
  }
}

// Path templates by record type, e.g. `/people/:id`, from the server. Without them, `records`
// blocks render as ordinary code.
export type RecordPaths = Record<string, string>;
export const RecordPathsContext = createContext<RecordPaths | null>(null);

type ChatRecord = { href: string; title: string; subtitle?: string };

// The model writes these blocks, so every value that reaches an href must be a positive integer.
function toRecord(item: unknown, paths: RecordPaths): ChatRecord | null {
  if (typeof item !== 'object' || item === null) {
    return null;
  }
  const fields = item as Record<string, unknown>;
  const template =
    typeof fields.type === 'string' &&
    Object.prototype.hasOwnProperty.call(paths, fields.type)
      ? paths[fields.type]
      : null;
  if (
    template === null ||
    typeof fields.title !== 'string' ||
    fields.title === ''
  ) {
    return null;
  }
  const placeholder = /:(\w+)/g;
  const ids = [...template.matchAll(placeholder)].map(([, key]) => fields[key]);
  if (!ids.every((id) => Number.isInteger(id) && (id as number) > 0)) {
    return null;
  }
  return {
    href: template.replace(placeholder, (_, key: string) =>
      String(fields[key])
    ),
    title: fields.title,
    subtitle: typeof fields.subtitle === 'string' ? fields.subtitle : undefined,
  };
}

function parseRecords(json: string, paths: RecordPaths): ChatRecord[] {
  try {
    const items: unknown = JSON.parse(json);
    if (!Array.isArray(items)) {
      return [];
    }
    return items.flatMap((item) => toRecord(item, paths) ?? []);
  } catch {
    return [];
  }
}

const RECORDS_BLOCK = /```records[^\n]*\n?([\s\S]*?)(\n```|$)/g;

type Segment = { text: string } | { records: ChatRecord[] } | { loading: true };

// Records render outside the bubble at full width, so they are cut out before Markdown runs.
function splitRecords(
  content: string,
  streaming: boolean,
  paths: RecordPaths | null
): Segment[] {
  if (paths === null) {
    return [{ text: content }];
  }
  const segments: Segment[] = [];
  let rest = 0;
  for (const match of content.matchAll(RECORDS_BLOCK)) {
    const [block, json, closingFence] = match;
    segments.push({ text: content.slice(rest, match.index) });
    const records = parseRecords(json, paths);
    if (records.length > 0) {
      segments.push({ records });
    } else if (closingFence || !streaming) {
      segments.push({ text: block });
    } else {
      segments.push({ loading: true });
    }
    rest = (match.index ?? 0) + block.length;
  }
  segments.push({ text: content.slice(rest) });
  return segments;
}

const renderer = {
  link(this: { elementId: string }, href: string, text: ReactNode) {
    const safe = safeHttpsUrl(href);
    return (
      <Link
        key={this.elementId}
        href={safe}
        rel="noopener noreferrer"
        target="_blank"
      >
        {text}
      </Link>
    );
  },
  image(this: { elementId: string }) {
    return <Fragment key={this.elementId} />;
  },
};

export function AssistantMessage({ content, animate = true, footer }: Props) {
  const debouncedContent = useChatTypewriter(content, 360, animate);
  const recordPaths = useContext(RecordPathsContext);

  return (
    <Container>
      {splitRecords(debouncedContent, animate, recordPaths).map(
        (segment, index) =>
          'loading' in segment ? (
            <RecordLoader key={index} aria-busy="true">
              <SkeletonLoader rows={3} columnsPerRow={1} variableWidths />
            </RecordLoader>
          ) : 'records' in segment ? (
            <RecordList key={index}>
              {segment.records.map((record, row) => (
                <li key={row}>
                  <RecordLink href={record.href}>
                    {record.title}
                    {typeof record.subtitle === 'string' && (
                      <RecordSubtitle>{record.subtitle}</RecordSubtitle>
                    )}
                  </RecordLink>
                </li>
              ))}
            </RecordList>
          ) : (
            segment.text.trim() !== '' && (
              <Bubble key={index}>
                <Markdown breaks renderer={renderer}>
                  {segment.text}
                </Markdown>
              </Bubble>
            )
          )
      )}
      {footer}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Tokens.spacing['0.5']};
`;

const Bubble = styled.div`
  ${assistantMessageBubbleStyles}
  ${assistantMessageContentStyles}
`;

const RecordList = styled.ul`
  align-self: stretch;
  list-style: none;
  margin: 0;
  padding: 0;
  background: ${FullPalette.WHITE};
  border: 1px solid ${FullPalette.GREY_E500};
  border-radius: ${Tokens.spacing['1']};

  li + li {
    border-top: 1px solid ${FullPalette.GREY_E500};
  }
`;

const RecordLoader = styled.div`
  align-self: stretch;
  padding: ${Tokens.spacing['1.5']} ${Tokens.spacing['2']};
  border: 1px solid ${FullPalette.GREY_E500};
  border-radius: ${Tokens.spacing['1']};
`;

const RecordLink = styled.a`
  ${chatMessageTextStyles}
  display: flex;
  flex-direction: column;
  padding: ${Tokens.spacing['1.5']} ${Tokens.spacing['2']};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    background: ${Tokens.colors.hover};
    color: ${FullPalette.EVERGREEN_700};
    text-decoration: none;
  }
`;

const RecordSubtitle = styled.span`
  font-size: 0.75rem;
  color: ${FullPalette.GREY_800};
`;
